import { createSelector, Selector } from 'reselect'

import { TariffState } from './tariff.reducer'

import { getTariffParams } from '../model/tariff.model'

import { selectDealer } from '~/agent/store/agent.selectors'
import { RootState } from '~/core/store/root.reducer'
import { selectProductSegment, selectTechnology, selectLineType } from '~/sale/store/sale.selectors'

type GetTariff = Selector<RootState, TariffState>

export const getTariffs: GetTariff = (state) => state.tariffs

const getId = (_: RootState, id: string) => id

export const selectTariffsCollection = createSelector(
  getTariffs,
  getId,
  (tariffs, id) => tariffs[id],
)

export const selectTariffs = createSelector(
  selectTariffsCollection,
  (tariff) => tariff?.tariffList.data || [],
)

export const selectSortedTariffs = createSelector(selectTariffs, (tariffs) => {
  return tariffs.slice().sort((a, b) => {
    return (
      (b.mobileData?.amount || 0) - (a.mobileData?.amount || 0) ||
      (b.call[0]?.amount || 0) - (a.call[0]?.amount || 0)
    )
  })
})

export const selectTariffError = createSelector(selectTariffsCollection, (tariffs) => ({
  errorCode: tariffs?.tariffList.errorCode,
  errorMessage: tariffs?.tariffList.errorMessage,
}))

export const selectTariffLoading = createSelector(
  selectTariffsCollection,
  (tariffs) => tariffs?.loading,
)

export const selectHasSelectedTariff = createSelector(
  selectTariffsCollection,
  (tariffs) => !!tariffs?.selectedTariffId,
)

export const selectSelectedTariff = createSelector(selectTariffsCollection, (tariffs) => {
  const id = tariffs?.selectedTariffId
  const list = tariffs?.tariffList.data
  return list ? list.find((elem) => elem.id === id) : undefined
})

export const selectTariffPermanentDiscounts = createSelector(selectSelectedTariff, (tariff) => {
  const tariffDiscounts = tariff?.discount
  const permanentDiscounts = tariffDiscounts?.filter((elem) => elem.duration?.amount === 0)
  return permanentDiscounts || []
})

export const selectHasPermanentDiscounts = createSelector(
  selectTariffPermanentDiscounts,
  (discounts) => !!discounts.length,
)

export const selectSelectedTariffName = createSelector(
  selectSelectedTariff,
  (tariff) => tariff?.name,
)

export const selectTariffPrice = createSelector(
  selectSelectedTariff,
  (tariff) => tariff?.price || [],
)

export const selectSelectedTariffId = createSelector(
  selectTariffsCollection,
  (tariffs) => tariffs.selectedTariffId,
)

export const selectTariffName = createSelector(selectSelectedTariff, (tariff) => tariff?.name)

export const selectTariffKeys = createSelector(getTariffs, (tariffState) => {
  return Object.keys(tariffState)
})

// this has more cases, we should complete them once we start making more flows
// https://mmsistemas.atlassian.net/browse/MSLS-336?focusedCommentId=740908&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-740908
export const getTariffParamSelector = createSelector(
  selectProductSegment,
  selectTechnology,
  selectLineType,
  selectDealer,
  (productSegment, technology, lineType, dealer): getTariffParams => ({
    commercialProfile: technology === 'mobileOnly' ? 'MOBILE' : 'CONVERGENT',
    customerType: 'Nuevo',
    subscriptionType: lineType === 'prepaid' ? 'PRE-PAGO' : 'POST-PAGO',
    billingType: lineType === 'prepaid' ? 'PREPAGO CLASICO' : 'POSPAGO',
    saleType: 'Venta',
    dealer,
    commercialSegment: productSegment === 'residential' ? 'Consumer' : 'SME',
  }),
)

export default selectTariffs
